import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderIcon from "@mui/icons-material/FolderOpenOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import {
  ownerBatchApi,
  type BatchView,
  type IngestReadiness,
  type OwnerShell,
} from "../../http/foyer";
import { uploadInBlocks } from "../../lib/blockUpload";
import { PageHeader } from "../../components/PageHeader";
import { packageFindingText } from "../../components/distribution/findings";
import VodWizardDialog from "../../components/distribution/VodWizardDialog";

/**
 * Upload — a supplier delivering their own material (`CONTENT-9`, `BE-36`).
 *
 * ## Why this page had to exist before the menu could grow
 *
 * "My titles" told a studio, verbatim, to *"send new material to your Nenda
 * contact"* — while the owner-scoped batch routes had been registered the whole
 * time and nothing in the client bound them. A partner-facing product whose
 * first instruction is "email somebody" is a product that has not shipped.
 *
 * ## The same handlers as staff bulk ingest, and deliberately fewer controls
 *
 * `ownerBatchApi` is `batchCalls("owner")` — the identical server code with the
 * owner derived from the session, never from the path. Two differences, both of
 * them the server's:
 *
 *   - **No package picker.** `/batches/:id/packages` is registered only on the
 *     staff surface, because the pin is written at screening. Offering the
 *     control here would promise something that 403s on every click.
 *   - **Attribution is not asked for.** An upload is filed against the owner
 *     the session belongs to, never the one the metadata claims — which is the
 *     whole point of `BE-36` and the reason a supplier cannot mis-file.
 *
 * ## Scanning before starting, always
 *
 * The flow is create → upload → scan → read the findings → start. A supplier
 * who starts an unscanned package finds out what was wrong with it from a
 * transcoder error hours later, and the thing they most need to see — this file
 * has no metadata, this title is a duplicate — is exactly what the scan says.
 */

interface ShellContext {
  shell: OwnerShell;
}

/** A directory picker prefixes the chosen folder's own name; drop it. */
function relativePathOf(file: File): string {
  const withPath = file as File & { webkitRelativePath?: string };
  const raw = withPath.webkitRelativePath || file.name;
  const slash = raw.indexOf("/");
  return slash > 0 ? raw.slice(slash + 1) : raw;
}

export default function SupplierUploadPage() {
  useOutletContext<ShellContext>();
  const [batches, setBatches] = useState<BatchView[] | null>(null);
  const [readiness, setReadiness] = useState<IngestReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [name, setName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  /** The single film being taken through the wizard, or null (`ART-9`). */
  const [wizardFile, setWizardFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await ownerBatchApi.listBatches();
      setBatches(res.batches);
      setReadiness(res.readiness);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 🚨 Opens the picker SYNCHRONOUSLY, and creates nothing yet.
   *
   * The first build awaited `createBatch` and `load()` and only then called
   * `.click()` on the hidden input. A browser only honours a programmatic
   * click on a file input while the user activation from the real click is
   * still live, and an `await` ends it — so the dialog never opened, silently,
   * while an empty delivery appeared in the list below with a disabled "Send
   * to Nenda". Reported exactly that way: *"it just goes down to the rows
   * below, disabled button send to nenda"*.
   *
   * Nothing may be awaited between the operator's click and this call.
   */
  const pick = (which: "files" | "folder") => {
    setError(null);
    (which === "folder" ? folderRef : fileRef).current?.click();
  };

  /**
   * The delivery is created once there are files to put in it.
   *
   * Also the reason a cancelled dialog leaves nothing behind: an empty batch
   * per abandoned click is a list nobody can read a week later.
   */
  const upload = async (files: FileList) => {
    setError(null);
    const list = Array.from({ length: files.length }, (_, i) => files[i]);
    try {
      setBusy("Creating the delivery…");
      const { batch } = await ownerBatchApi.createBatch({ name: name.trim() });
      setName("");

      for (const [index, file] of list.entries()) {
        setBusy(`Uploading ${index + 1} of ${list.length}: ${file.name}`);
        // Sequential on purpose, matching the portal's own bulk client: a
        // package that takes an hour must not saturate a partner's line.
        const { uploadUrl } = await ownerBatchApi.batchUploadUrl(
          batch.id,
          relativePathOf(file)
        );
        await uploadInBlocks(uploadUrl, file, {
          contentType: file.type || "application/octet-stream",
        });
      }

      setBusy("Checking the delivery…");
      await ownerBatchApi.scanBatch(batch.id);
      await load();
    } catch (e) {
      const err = e as { code?: string; message: string };
      setError(
        err.code === "name_required"
          ? "Give the delivery a name — a list of deliveries called Untitled is one nobody can operate a week later."
          : err.code === "storage_not_configured"
            ? "Uploads are not switched on for this account yet. Your Nenda contact can turn them on."
            : err.code === "unsafe_path"
              ? "One of those filenames cannot be stored. Rename it and try again."
              : err.message
      );
      await load();
    } finally {
      setBusy(null);
    }
  };

  /**
   * Throw an unsent delivery away (`ART-7`).
   *
   * Not a confirmation dialog: a DRAFT delivery is staging, nothing in the
   * catalogue points at it, and the server refuses outright once it has been
   * started — so the destructive case this would guard against cannot be
   * reached from here. Guarding it anyway would be ceremony over an action
   * whose whole reason for existing is that somebody is stuck with a row they
   * cannot use.
   */
  const remove = async (batch: BatchView) => {
    setBusy("Deleting the delivery…");
    setError(null);
    try {
      await ownerBatchApi.deleteBatch(batch.id);
      await load();
    } catch (e) {
      const err = e as { code?: string; message: string };
      setError(
        err.code === "batch_not_draft"
          ? "This delivery has already been sent, so it cannot be deleted. Cancel it instead if it should not go ahead."
          : err.code === "blobs_not_removed"
            ? // The row is still there ON PURPOSE — it is the only handle left
              // on the files that would not go — so the honest instruction is
              // "press it again", not a shrug.
              "Some of the uploaded files could not be removed, so the delivery is still here. Try Delete again in a moment."
            : err.message
      );
      await load();
    } finally {
      setBusy(null);
    }
  };

  const start = async (batch: BatchView) => {
    setBusy("Sending it to Nenda…");
    setError(null);
    try {
      await ownerBatchApi.startBatch(batch.id);
      await load();
    } catch (e) {
      const err = e as { code?: string; message: string };
      setError(
        err.code === "nothing_to_start"
          ? "Every file in this delivery was refused by the check, so there is nothing to send."
          : err.message
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack spacing={3}>
      {/* The wizard is a Dialog, so one film is guided IN PLACE — no
          navigation, and nothing to carry across a route change. */}
      <VodWizardDialog
        open={wizardFile !== null}
        surface="owner"
        initialFile={wizardFile}
        onClose={() => setWizardFile(null)}
        onFinished={() => {}}
      />

      <PageHeader
        eyebrow="Your account"
        title="Upload"
        subtitle="Send titles to Nenda. One film is taken through step by step; a folder or several files at once is sent as a delivery, and we check what arrives and tell you what is missing before anything is sent."
      />

      {error ? <Alert severity="error">{error}</Alert> : null}

      {/* The transcoder's own verdict, carried rather than guessed at. A
          delivery queued behind a dispatcher that cannot run is waiting
          differently from one that is simply next. */}
      {readiness && !readiness.ready ? (
        <Alert severity="warning">
          Deliveries can be uploaded, but Nenda cannot process them right now.
          Yours will be picked up as soon as that clears.
        </Alert>
      ) : null}

      {busy ? (
        <Box>
          <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 0.5 }}>
            {busy}
          </Typography>
          <LinearProgress />
        </Box>
      ) : null}

      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 2,
          p: 2.5,
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1 }}>New delivery</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            size="small"
            label="What is in it?"
            placeholder="Autumn 2026 features"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ flex: 1 }}
            inputProps={{ "data-testid": "batch-name" }}
          />
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={name.trim() === "" || busy !== null}
            onClick={() => pick("files")}
            data-testid="batch-create"
          >
            Choose files
          </Button>
          <Button
            variant="outlined"
            startIcon={<FolderIcon />}
            disabled={name.trim() === "" || busy !== null}
            onClick={() => pick("folder")}
            data-testid="batch-create-folder"
          >
            Choose folder
          </Button>
        </Stack>

        {/* 🚨 Two pickers, because the scanner genuinely accepts both.
            A bare video at the root is the FLAT layout — one title, its name
            taken from the filename — and metadata is optional throughout. A
            folder per title is the FOLDER layout, which is what you want when
            a title carries its own JSON, posters and subtitles.

            The first build offered only a directory picker, which imposed a
            constraint the system does not have: it made "I just want to send
            you this one film" impossible, and there was no way to discover
            that a single file would have worked. */}
        <Typography
          variant="caption"
          sx={{ color: tokens.inkFaint, display: "block", mt: 1 }}
        >
          {/* 🚨 The accepted formats, BEFORE the upload rather than after.
              Only `.mp4` is classified as a video (`PACKAGE_VIDEO_EXTENSIONS`),
              so a .mov test clip uploads perfectly and then scans to nothing —
              which is exactly how "0 titles" happens with no error in sight. */}
          One film is enough — pick a single <strong>.mp4</strong> and we take
          you through it step by step: its details, subtitles, description and
          artwork, then you hand it in. A folder, or several files at once, is
          sent as a <strong>delivery</strong> instead: a folder per title lets
          you send its metadata, posters (.jpg/.png/.webp) and subtitles
          (.vtt/.srt) with it, and anything else is carried but not turned into
          a title.
        </Typography>

        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          data-testid="batch-files"
          onChange={(e) => {
            const files = e.target.files;
            e.target.value = "";
            if (!files?.length) return;
            /* 🚨 ONE film is not a delivery (`ART-9`).
               The batch machinery earns its keep for a package with its own
               layout, sidecars, posters and subtitles. For a single file it
               strictly loses: no metadata step, no subtitles, no artwork, and
               the title's name guessed from a filename instead of asked for.
               So one file opens the wizard; everything else is a delivery. */
            if (files.length === 1) {
              setWizardFile(files[0]);
              return;
            }
            void upload(files);
          }}
        />
        <input
          ref={folderRef}
          type="file"
          multiple
          hidden
          data-testid="batch-folder"
          {...({
            webkitdirectory: "",
            directory: "",
          } as Record<string, string>)}
          onChange={(e) => {
            if (e.target.files?.length) void upload(e.target.files);
            e.target.value = "";
          }}
        />
      </Box>

      {batches === null ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : batches.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 20, mb: 1 }}>
            No deliveries yet
          </Typography>
          <Typography
            sx={{ color: tokens.inkMuted, maxWidth: 460, mx: "auto" }}
          >
            Name a delivery above and choose the folder it lives in. We check
            it, show you anything that is missing, and only send it when you say
            so.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {batches.map((batch) => (
            <BatchCard
              key={batch.id}
              batch={batch}
              busy={busy !== null}
              onStart={() => void start(batch)}
              onDelete={() => void remove(batch)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * One delivery, and the only question that matters about it: can it be sent,
 * and if not, what is wrong with which file.
 */
function BatchCard({
  batch,
  busy,
  onStart,
  onDelete,
}: {
  batch: BatchView;
  busy: boolean;
  onStart: () => void;
  onDelete: () => void;
}) {
  const blocked = batch.items.filter((i) => i.blocked).length;
  /* 🚨 An ACCOUNT problem wearing a per-file costume.
     `supplier_not_bound` blocks every item in every delivery this account will
     ever make, and it is fixed by Nenda linking the account to a provider —
     not by the supplier touching their files. Repeating it once per row, in
     among genuine per-file findings, is how somebody spends an afternoon
     re-uploading a folder that was never the problem. */
  const notBound = batch.items.some((i) =>
    i.findings.some((f) => f.code === "supplier_not_bound")
  );
  const sent = batch.startedAt !== null;
  const scanned = batch.scannedAt !== null;

  /**
   * 🚨 Why "Send to Nenda" cannot be pressed — as a SENTENCE, not a boolean.
   *
   * The condition used to be `busy || !scanned || items.length === blocked`,
   * and that last term is `0 === 0` for a delivery whose scan found nothing.
   * So an empty package was permanently unsendable with no reason anywhere on
   * screen, which is exactly how this was reported: *"it uploaded and the file
   * ended up in the list but send to nenda is grayed, and I can't click it."*
   *
   * Two different situations were wearing the same disabled button:
   *
   *  - **nothing was found** — almost always a file the scanner does not
   *    classify as a video. Only `.mp4` is (`PACKAGE_VIDEO_EXTENSIONS`), so a
   *    `.mov`, an `.mkv` or a poster uploads perfectly and pairs to nothing.
   *  - **everything that was found is blocked** — the files are right, the
   *    findings below say what is wrong with them.
   *
   * The caption above the picker already warned about the first. That is the
   * wrong place: it is read before the mistake and forgotten by the time it
   * matters. The reason belongs beside the control that will not move.
   */
  const cannotSend = !scanned
    ? "This delivery has not been checked yet."
    : batch.items.length === 0
      ? "Nothing in this delivery was recognised as a title. Only .mp4 is read as a video, so a .mov, an .mkv or a picture on its own uploads fine and pairs to nothing. Delete it and send an .mp4, or use New title to add one film with its metadata."
      : batch.items.length === blocked
        ? "Every title in this delivery is blocked. The findings below say what is wrong with each one."
        : null;

  return (
    <Box
      sx={{
        background: tokens.surface,
        border: `1px solid ${tokens.divider}`,
        borderRadius: 2,
        p: 2.5,
      }}
      data-testid={`batch-${batch.id}`}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{batch.name}</Typography>
          <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
            {batch.items.length} title{batch.items.length === 1 ? "" : "s"}
            {/* 🚨 "uploading" only while nothing has been checked AND nothing
                has been found. A delivery whose scan failed used to sit at
                "0 titles · uploading" for ever, which reads as work in
                progress — so nobody looks for the reason, and the reason was
                not on screen either (see packageFindings below). */}
            {sent
              ? " · sent to Nenda"
              : scanned
                ? " · checked"
                : " · waiting to be checked"}
          </Typography>
        </Box>
        {!sent ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {/* A delivery that was never sent is the caller's to throw away —
                and the empty one they got from an unreadable file is the whole
                reason this exists. `cancel` is the verb for a STARTED batch
                and keeps the record; this removes it, bytes and all. */}
            <Button
              size="small"
              color="error"
              disabled={busy}
              onClick={onDelete}
              data-testid={`batch-delete-${batch.id}`}
            >
              Delete
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={busy || cannotSend !== null}
              onClick={onStart}
              data-testid={`batch-start-${batch.id}`}
            >
              Send to Nenda
            </Button>
          </Stack>
        ) : (
          <Chip size="small" label="Sent" />
        )}
      </Stack>

      {/* The reason, beside the control it explains. A disabled button with no
          sentence next to it is the defect this replaces. */}
      {!sent && cannotSend && (
        <Alert
          severity={batch.items.length === 0 ? "warning" : "info"}
          sx={{ mb: 1.5 }}
          data-testid={`batch-cannot-send-${batch.id}`}
        >
          {cannotSend}
        </Alert>
      )}

      {/* 🚨 The blocked count is the headline, not a detail in a row. A
          supplier who sends a delivery believing it complete, and learns
          three of twelve were dropped, learns it from a missing title on a
          television. */}
      {/* 🚨 The findings about the DELIVERY, not about a file in it.
          `no_video_files` and `package_empty` live here, and with zero items
          there is no row to hang them on — so a scan that found nothing
          rendered as a silent "0 titles" with its own explanation sitting
          unread on the wire. This is the first thing to draw, because when it
          fires there is nothing else on the card. */}
      {batch.packageFindings.length > 0 ? (
        <Alert
          severity={
            batch.packageFindings.some((f) => f.severity === "blocking")
              ? "error"
              : "info"
          }
          sx={{ mb: 1.5 }}
          data-testid={`batch-findings-${batch.id}`}
        >
          <Stack spacing={0.25}>
            {batch.packageFindings.map((f, i) => (
              <Typography key={`${f.code}-${i}`} sx={{ fontSize: 13 }}>
                {packageFindingText(f)}
              </Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      {notBound ? (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="not-bound">
          This account is not linked to a supplier yet, so there is nothing to
          file your titles under. Nenda has to make that link before anything
          can be sent — your files are fine and stay where they are.
        </Alert>
      ) : blocked > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {blocked} of {batch.items.length} cannot be sent yet. Fix the folder
          and upload it again — the rest will still go.
        </Alert>
      ) : null}

      {batch.items.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: tokens.inkMuted }}>Title</TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>File</TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>State</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batch.items.map((item) => (
                <TableRow key={item.key}>
                  <TableCell>{item.title || item.key}</TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      color: tokens.inkFaint,
                    }}
                  >
                    {item.videoPath}
                  </TableCell>
                  <TableCell>
                    {item.blocked ? (
                      <Stack spacing={0.25}>
                        <Chip
                          size="small"
                          label="Cannot be sent"
                          color="error"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                        {/* The reason, in the supplier's own words rather than
                            a code they would have to ask us about. */}
                        {item.findings.slice(0, 2).map((f, i) => (
                          <Typography
                            key={i}
                            variant="caption"
                            sx={{ color: tokens.inkFaint }}
                          >
                            {packageFindingText(f)}
                          </Typography>
                        ))}
                      </Stack>
                    ) : item.jobError ? (
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.error }}
                      >
                        {item.jobError}
                      </Typography>
                    ) : item.phase ? (
                      <Chip
                        size="small"
                        label={item.phase}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Ready"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
}
